/* eslint-disable max-len */
import React from 'react';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';
// formik
import { Formik } from 'formik';
import * as Yup from 'yup';
// @material-ui/core components
import withStyles from '@material-ui/core/styles/withStyles';
import TextField from '@material-ui/core/TextField';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Checkbox from '@material-ui/core/Checkbox';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import InputLabel from '@material-ui/core/InputLabel';
import FormControl from '@material-ui/core/FormControl';
import FormHelperText from '@material-ui/core/FormHelperText';
import validationFormsStyle from 'assets/jss/material-dashboard-pro-react/views/validationFormsStyle';
// @material-ui/icons
import Check from '@material-ui/icons/Check';
// core components
import Card from 'components/UI/Card/Card';
import GridItem from 'components/UI/Grid/GridItem';
import CardBody from 'components/UI/Card/CardBody';
import Snackbars from 'components/UI/Snackbar/Snackbar';
import Button from 'components/UI/CustomButtons/Button';
import GridContainer from 'components/UI/Grid/GridContainer';
// our components
import { NOTIFICATION_TYPE } from 'routes/paths';
import RouterContext from 'helpers/routerContext';
import Loading from 'components/common/Loading';

/**
 * @description
 * Validation form's notification type.
 * @param {Function} t - Translation function from HOC.
 * @returns {Object} - YUP shape.
 */
const translateValidationSchema = t =>
  Yup.object({
    name: Yup.string().required(t('errors.required')),
    description: Yup.string().required(t('errors.required')),
    category: Yup.string().required(t('errors.required')),
    strategy: Yup.string()
      .oneOf(['INSTANT', 'GROUPED'], t('errors.invalid'))
      .required(t('errors.required')),
    parameters: Yup.string().test(
      'is-json',
      t('errors.invalid_json'),
      value => {
        if (!value) return true; // Allow empty value
        try {
          JSON.parse(value);
          return true;
        } catch (e) {
          return false;
        }
      },
    ),
  });

/**
 * @class
 * @extends React.Component
 */
class NotificationTypeForm extends React.Component {
  static contextType = RouterContext;

  constructor(props) {
    super(props);

    const {
      id,
      name,
      description,
      photo,
      category,
      enableSendPush,
      enableSendEmail,
      defaultUserSettingsEmail,
      defaultUserSettingsPush,
      strategy,
      example,
      parameters,
      isCritical,
    } = props;

    this.state = {
      fields: {
        id,
        name,
        description,
        photo,
        category,
        enableSendPush,
        enableSendEmail,
        defaultUserSettingsEmail,
        defaultUserSettingsPush,
        strategy,
        example,
        parameters,
        isCritical,
      },
    };

    this.formikRef = null;
  }

  /**
   * @description
   * Handle redirect of cancel btn.
   */
  handleCancelBtn = () => {
    const history = this.context;
    history.push(NOTIFICATION_TYPE.LIST);
  };

  /**
   * @description
   * Set server errors with setErrors function of Formik.
   * @param {*} serverErrors - Api errors.
   */
  setServerErrors = serverErrors => {
    let all = null;
    if (this.formikRef && serverErrors.length) {
      const errors = {};

      serverErrors.forEach(item => {
        errors[item.field] = item.messages;
        if (item.field === '__all__') {
          all = item.messages;
        }
      });

      setTimeout(() => this.formikRef && this.formikRef.setErrors(errors), 0);
    }
    return all;
  };

  /**
   * @description
   * Render component.
   *
   * @returns {*} - NotificationTypeForm component.
   */
  render() {
    const { fields } = this.state;
    const {
      classes,
      t,
      id,
      handleNotificationTypeSave,
      submitLoading,
      errors,
    } = this.props;
    const allError = this.setServerErrors(errors);

    return (
      <Formik
        ref={node => {
          this.formikRef = node;
        }}
        initialValues={fields}
        validationSchema={translateValidationSchema(t)}
        onSubmit={values => handleNotificationTypeSave(values)}
        enableReinitialize
      >
        {({
          values,
          errors: formErrors,
          touched,
          handleChange,
          handleBlur,
          handleSubmit,
          setFieldValue,
        }) => (
          <form onSubmit={handleSubmit}>
            <GridContainer>
              <GridItem xs={12}>
                <Card>
                  <CardBody>
                    <GridContainer>
                      <GridItem xs={12}>
                        <h4 className={classes.cardIconTitle}>
                          {id
                            ? t('notification_type.form.edit_title')
                            : t('notification_type.form.create_title')}
                        </h4>
                      </GridItem>
                      <GridItem xs={12} sm={6}>
                        <TextField
                          id="name"
                          name="name"
                          label={`${t('notification_type.form.name')} *`}
                          value={values.name}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={touched.name && Boolean(formErrors.name)}
                          helperText={touched.name && formErrors.name}
                          fullWidth
                          margin="normal"
                        />
                      </GridItem>
                      <GridItem xs={12} sm={6}>
                        <TextField
                          id="category"
                          name="category"
                          label={`${t('notification_type.form.categoryId')} *`}
                          type="number"
                          value={values.category}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={
                            touched.category && Boolean(formErrors.category)
                          }
                          helperText={touched.category && formErrors.category}
                          fullWidth
                          margin="normal"
                        />
                      </GridItem>
                      <GridItem xs={12}>
                        <TextField
                          id="description"
                          name="description"
                          label={`${t('notification_type.form.description')} *`}
                          value={values.description}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={
                            touched.description &&
                            Boolean(formErrors.description)
                          }
                          helperText={
                            touched.description && formErrors.description
                          }
                          fullWidth
                          margin="normal"
                          multiline
                          rows={3}
                        />
                      </GridItem>
                      <GridItem xs={12}>
                        <TextField
                          id="photo"
                          name="photo"
                          label={t('notification_type.form.photo')}
                          value={values.photo}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          fullWidth
                          margin="normal"
                        />
                      </GridItem>
                      <GridItem xs={12}>
                        <FormControl
                          fullWidth
                          margin="normal"
                          error={
                            touched.strategy && Boolean(formErrors.strategy)
                          }
                        >
                          <InputLabel htmlFor="strategy">
                            {`${t('notification_type.form.strategy')} *`}
                          </InputLabel>
                          <Select
                            value={values.strategy}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            inputProps={{
                              name: 'strategy',
                              id: 'strategy',
                            }}
                          >
                            <MenuItem value="INSTANT">INSTANT</MenuItem>
                            <MenuItem value="GROUPED">GROUPED</MenuItem>
                          </Select>
                          {touched.strategy && formErrors.strategy && (
                            <FormHelperText>
                              {formErrors.strategy}
                            </FormHelperText>
                          )}
                        </FormControl>
                      </GridItem>
                      <GridItem xs={12}>
                        <TextField
                          id="example"
                          name="example"
                          label={t('notification_type.form.example')}
                          value={values.example}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          fullWidth
                          margin="normal"
                          multiline
                          rows={3}
                        />
                      </GridItem>
                      <GridItem xs={12}>
                        <TextField
                          id="parameters"
                          name="parameters"
                          label={t('notification_type.form.parameters')}
                          value={values.parameters}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={
                            touched.parameters && Boolean(formErrors.parameters)
                          }
                          helperText={
                            touched.parameters && formErrors.parameters
                          }
                          fullWidth
                          margin="normal"
                          multiline
                          rows={5}
                          placeholder='{"key": "value"}'
                        />
                      </GridItem>
                      <GridItem xs={12}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checkedIcon={
                                <Check className={classes.checkedIcon} />
                              }
                              icon={<Check className={classes.uncheckedIcon} />}
                              classes={{
                                checked: classes.checked,
                              }}
                              checked={values.isCritical}
                              onChange={e => {
                                setFieldValue('isCritical', e.target.checked);
                              }}
                            />
                          }
                          label={t('notification_type.form.isCritical')}
                        />
                      </GridItem>
                      <GridItem xs={12}>
                        <h5 className={classes.cardIconTitle}>
                          {t('notification_type.form.deliveryLabel')}
                        </h5>
                      </GridItem>
                      <GridItem xs={12} sm={6}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checkedIcon={
                                <Check className={classes.checkedIcon} />
                              }
                              icon={<Check className={classes.uncheckedIcon} />}
                              classes={{
                                checked: classes.checked,
                              }}
                              checked={values.enableSendPush}
                              onChange={e => {
                                setFieldValue(
                                  'enableSendPush',
                                  e.target.checked,
                                );
                              }}
                            />
                          }
                          label={t('notification_type.form.deliveryPush')}
                        />
                      </GridItem>
                      <GridItem xs={12} sm={6}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checkedIcon={
                                <Check className={classes.checkedIcon} />
                              }
                              icon={<Check className={classes.uncheckedIcon} />}
                              classes={{
                                checked: classes.checked,
                              }}
                              checked={values.enableSendEmail}
                              onChange={e => {
                                setFieldValue(
                                  'enableSendEmail',
                                  e.target.checked,
                                );
                              }}
                            />
                          }
                          label={t('notification_type.form.deliveryEmail')}
                        />
                      </GridItem>
                      <GridItem xs={12}>
                        <h5 className={classes.cardIconTitle}>
                          {t('notification_type.form.defaultUserSettingsLabel')}
                        </h5>
                      </GridItem>
                      <GridItem xs={12} sm={6}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checkedIcon={
                                <Check className={classes.checkedIcon} />
                              }
                              icon={<Check className={classes.uncheckedIcon} />}
                              classes={{
                                checked: classes.checked,
                              }}
                              checked={values.defaultUserSettingsPush}
                              onChange={e => {
                                setFieldValue(
                                  'defaultUserSettingsPush',
                                  e.target.checked,
                                );
                              }}
                            />
                          }
                          label={t(
                            'notification_type.form.defaultSettingsPush',
                          )}
                        />
                      </GridItem>
                      <GridItem xs={12} sm={6}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checkedIcon={
                                <Check className={classes.checkedIcon} />
                              }
                              icon={<Check className={classes.uncheckedIcon} />}
                              classes={{
                                checked: classes.checked,
                              }}
                              checked={values.defaultUserSettingsEmail}
                              onChange={e => {
                                setFieldValue(
                                  'defaultUserSettingsEmail',
                                  e.target.checked,
                                );
                              }}
                            />
                          }
                          label={t(
                            'notification_type.form.defaultSettingsEmail',
                          )}
                        />
                      </GridItem>
                      <GridItem xs={12}>
                        <div className={classes.formCategory}>
                          <small>*</small>{' '}
                          {t('notification_type.form.required_fields')}
                        </div>
                      </GridItem>
                      <GridItem xs={12}>
                        <div className={classes.center}>
                          <Button
                            id="cancel-notification-type"
                            color="rose"
                            onClick={this.handleCancelBtn}
                            data-cy="cancel-btn"
                          >
                            {t('notification_type.form.cancel')}
                          </Button>
                          <Button
                            id="save-notification-type"
                            color="success"
                            type="submit"
                            disabled={submitLoading}
                            data-cy="save-btn"
                          >
                            {id
                              ? t('notification_type.form.update')
                              : t('notification_type.form.save')}
                          </Button>
                        </div>
                      </GridItem>
                    </GridContainer>
                  </CardBody>
                </Card>
              </GridItem>
            </GridContainer>
            {allError && (
              <Snackbars
                place="tc"
                color="danger"
                icon="error"
                message={allError}
                open
                closeNotification={() => {}}
                close
              />
            )}
            {submitLoading && <Loading />}
          </form>
        )}
      </Formik>
    );
  }
}

/**
 * NotificationTypeForm PropTypes
 */
NotificationTypeForm.propTypes = {
  classes: PropTypes.instanceOf(Object).isRequired,
  t: PropTypes.func.isRequired,
  id: PropTypes.string,
  name: PropTypes.string,
  description: PropTypes.string,
  photo: PropTypes.string,
  category: PropTypes.string,
  enableSendPush: PropTypes.bool,
  enableSendEmail: PropTypes.bool,
  defaultUserSettingsEmail: PropTypes.bool,
  defaultUserSettingsPush: PropTypes.bool,
  strategy: PropTypes.string,
  example: PropTypes.string,
  parameters: PropTypes.string,
  isCritical: PropTypes.bool,
  handleNotificationTypeSave: PropTypes.func.isRequired,
  submitLoading: PropTypes.bool,
  errors: PropTypes.instanceOf(Array),
};

NotificationTypeForm.defaultProps = {
  id: null,
  name: '',
  description: '',
  photo: '',
  category: '',
  enableSendPush: false,
  enableSendEmail: false,
  defaultUserSettingsEmail: false,
  defaultUserSettingsPush: false,
  strategy: 'INSTANT',
  example: '',
  parameters: '',
  isCritical: false,
  submitLoading: false,
  errors: [],
};

export default withTranslation()(
  withStyles(validationFormsStyle)(NotificationTypeForm),
);
